/**
 * CihHero — hero block for CCO, CTP, and Framework detail pages.
 *
 * Grid: title-left / meta-column-right on md+. Stacks on mobile. The meta
 * column shows an optional logo tile (matches the card's width and corner
 * radius) followed by whatever `meta` is passed (typically CihAtAGlanceCard).
 *
 * Aliases: hero, cih hero, entity hero
 */

import { motion } from "motion/react";
import type { ReactNode } from "react";

import { Breadcrumb } from "~/components/ui/breadcrumb";
import { CDNImage } from "~/components/ui/cdn-image";
import { useReducedMotionSafe } from "~/lib/hooks/use-reduced-motion-safe";

type BreadcrumbItem = { label: string; href?: string };

type Props = {
  readonly breadcrumb: BreadcrumbItem[];
  readonly kicker: string;
  readonly kickerColorClass?: string; // e.g., "text-purple-700"
  readonly title: string;
  readonly lede?: string | null;
  readonly cta?: ReactNode;
  readonly meta?: ReactNode; // typically <CihAtAGlanceCard />
  readonly logoUrl?: string | null;
};

export function CihHero({
  breadcrumb,
  kicker,
  kickerColorClass = "text-purple-700",
  title,
  lede,
  cta,
  meta,
  logoUrl,
}: Props) {
  const reducedMotion = useReducedMotionSafe();
  const hasMetaColumn = logoUrl || meta;

  return (
    <section className="border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 pt-6 pb-12 md:pt-8 md:pb-16 xl:px-0">
        <Breadcrumb items={breadcrumb} isBold={false} />
        <motion.div
          initial={reducedMotion ? false : { opacity: 0, y: 18 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mt-6 grid gap-10 md:grid-cols-[1fr_320px] md:items-end md:gap-16"
        >
          <div>
            <div
              className={`text-xs font-medium tracking-[0.22em] uppercase ${kickerColorClass}`}
            >
              ● {kicker}
            </div>
            <h1 className="mt-4 font-heading text-4xl leading-none font-medium tracking-tight text-gray-900 md:text-5xl lg:text-6xl xl:text-7xl">
              {title}
            </h1>
            {lede ? (
              <p className="mt-5 max-w-xl text-lg leading-normal text-gray-700">
                {lede}
              </p>
            ) : null}
            {cta ? (
              <div className="mt-7 flex flex-wrap items-center gap-3">
                {cta}
              </div>
            ) : null}
          </div>
          {hasMetaColumn ? (
            <div className="space-y-4">
              {logoUrl ? (
                <div className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-6">
                  <CDNImage
                    src={logoUrl}
                    alt=""
                    width={320}
                    height={96}
                    className="h-20 w-auto max-w-full object-contain md:h-24"
                  />
                </div>
              ) : null}
              {meta}
            </div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}
